import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { get } from 'lodash';
import { Modal } from 'react-bootstrap';
import { useJsApiLoader } from '@react-google-maps/api';
import Delete from '../../../../img/icons/svg/botones/delete.png';
import './sccs/FormCalculadoraKilometraje.scss';
import { moneyFormatter } from "@/services/utils/useUtils";
import useGetInfoKilometrajeSolicitudGastosTransporte from 'services/record/SolicitudGastosTransporte/useGetInfoKilometrajeSolicitudGastosTransporte';
interface IGooglePlaceAutocompleteProps {
  id: string;
  initialValue?: string;
  isGoogleMapsLoaded: boolean;
  hasSelectedPlace: boolean;
  label: string;
  placeholder: string;
  onUsePersonalAddress?: () => void;
  personalAddressDisabled?: boolean;
  placeIdRef: React.RefObject<string | null>;
  valueRef: React.RefObject<IGooglePlaceAutocompleteElement | null>;
  onChange: (value: string) => void;
  onError: (message: string) => void;
}

interface IGooglePlaceAutocompleteElement extends HTMLElement {
  description?: string;
  disabled?: boolean;
  includedRegionCodes?: string[];
  noInputIcon?: boolean;
  placeholder?: string;
  requestedLanguage?: string;
  requestedRegion?: string;
  value: string;
}

interface IGooglePlacesNewLibrary {
  PlaceAutocompleteElement: new (options?: {
    includedRegionCodes?: string[];
    placeholder?: string;
    requestedLanguage?: string;
    requestedRegion?: string;
  }) => IGooglePlaceAutocompleteElement;
}

interface IPlacePredictionSelectEvent extends Event {
  placePrediction?: {
    placeId?: string;
    text?: { text?: string };
    toPlace: () => google.maps.places.Place;
  };
}

interface IGoogleRoutesLibrary {
  Route: {
    computeRoutes: (request: {
      destination: string | google.maps.places.Place;
      fields: string[];
      language: string;
      origin: string | google.maps.places.Place;
      region: string;
      routingPreference: string;
      travelMode: string;
    }) => Promise<{ routes?: IGoogleRoute[] }>;
  };
}

interface IGoogleRoute {
  createPolylines: () => google.maps.Polyline[];
  distanceMeters?: number;
  viewport?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;
}

interface IRoutePreviewMapProps {
  isGoogleMapsLoaded: boolean;
  route: IGoogleRoute | null;
}

interface IVehicleTripValue {
  Id?: string | null;
  FechaViaje: string | null;
  IdaVuelta: boolean;
  DireccionOrigen: string;
  DireccionDestino: string;
  Kilometros: number;
  PrecioKm: number;
  Importe: number;
}

interface ICalculatedTrip {
  origin: string;
  destination: string;
  kilometers: number;
}

interface IModalValidationErrors {
  travelDate?: string;
  origin?: string;
  destination?: string;
  calculation?: string;
}

const kilometersFormatter = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});


const routePreviewCenter = { lat: 40.4168, lng: -3.7038 };

const getUnknownErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallbackMessage;
};

const parseNumberValue = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalizedValue = String(value ?? '').trim().replace(',', '.');
  if (!normalizedValue) return null;

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatDateForInput = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  return value.split('T')[0];
};

const getTodayInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseBooleanValue = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    return ['true', '1', 'si', 'sí', 's'].includes(normalizedValue);
  }

  return false;
};

const roundToTwoDecimals = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const calculateTripAmount = (kilometers: number, pricePerKm: number, isRoundTrip: boolean) => (
  roundToTwoDecimals(kilometers * pricePerKm * (isRoundTrip ? 2 : 1))
);

const formatDateForDisplay = (value: unknown) => {
  const valueAsString = typeof value === 'string' ? value : '';
  const [year, month, day] = valueAsString.split('-');

  if (!year || !month || !day) {
    return '-';
  }

  return `${day}/${month}/${year}`;
};

const formatKilometers = (value: unknown) => {
  const numericValue = parseNumberValue(value);
  return numericValue === null ? '-' : `${kilometersFormatter.format(numericValue)} km`;
};

const formatMoney = (value: unknown) => {
  const numericValue = parseNumberValue(value);
  return numericValue === null ? '-' : moneyFormatter.format(numericValue);
};

const formatPricePerKm = (value: unknown) => {
  const numericValue = parseNumberValue(value);
  return numericValue === null ? '-' : `${moneyFormatter.format(numericValue)}/km`;
};

const getValidationMessages = (errors: IModalValidationErrors) => (
  Object.values(errors).filter((message): message is string => typeof message === 'string' && message.trim() !== '')
);

export const adaptCalculadoraKilometraje = (trips: unknown): IVehicleTripValue[] => {
  if (!Array.isArray(trips)) return [];

  return trips.map((trip) => {
    const tripData = trip as Record<string, unknown>;
    const kilometers = parseNumberValue(tripData.Kms) ?? 0;
    const pricePerKm = parseNumberValue(tripData.PrecioKms) ?? 0;
    const isRoundTrip = parseBooleanValue(tripData.IdayVuelta);

    return {
      Id: (tripData.Id as string | null | undefined) ?? null,
      FechaViaje: formatDateForInput(tripData.FechaViaje),
      IdaVuelta: isRoundTrip,
      DireccionOrigen: String(tripData.OrigenViaje ?? ''),
      DireccionDestino: String(tripData.DestinoViaje ?? ''),
      Kilometros: kilometers,
      PrecioKm: pricePerKm,
      Importe: parseNumberValue(tripData.Importe) ?? calculateTripAmount(kilometers, pricePerKm, isRoundTrip),
    };
  });
};

export const exportCalculadoraKilometraje = (trips: unknown) => {
  if (!Array.isArray(trips)) return [];

  return trips.map((trip) => {
    const tripData = trip as Partial<IVehicleTripValue>;
    const kilometers = parseNumberValue(tripData.Kilometros) ?? 0;
    const pricePerKm = parseNumberValue(tripData.PrecioKm) ?? 0;
    const isRoundTrip = parseBooleanValue(tripData.IdaVuelta);

    return {
      Id: tripData.Id ?? null,
      FechaViaje: tripData.FechaViaje ?? null,
      IdayVuelta: isRoundTrip,
      OrigenViaje: tripData.DireccionOrigen ?? null,
      DestinoViaje: tripData.DireccionDestino ?? null,
      Kms: kilometers,
      PrecioKms: pricePerKm,
      Importe: parseNumberValue(tripData.Importe) ?? calculateTripAmount(kilometers, pricePerKm, isRoundTrip),
    };
  });
};

const RoutePreviewMap: React.FC<IRoutePreviewMapProps> = ({ isGoogleMapsLoaded, route }) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const polylinesRef = useRef<google.maps.Polyline[]>([]);
  const markersRef = useRef<google.maps.Marker[]>([]);

  useEffect(() => {
    if (!isGoogleMapsLoaded || !route || !mapContainerRef.current) {
      return;
    }

    const clearRouteOverlays = () => {
      polylinesRef.current.forEach((polyline) => polyline.setMap(null));
      polylinesRef.current = [];
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapContainerRef.current, {
        center: routePreviewCenter,
        zoom: 6,
        mapTypeId: 'roadmap',
        clickableIcons: true,
        disableDefaultUI: true,
        disableDoubleClickZoom: true,
        draggable: true,
        gestureHandling: 'none',
        keyboardShortcuts: false,
        scrollwheel: true,
        streetViewControl: false,
      });
    }

    const map = mapRef.current;

    map.setOptions({
      clickableIcons: true,
      disableDefaultUI: true,
      disableDoubleClickZoom: true,
      draggable: true,
      gestureHandling: 'none',
      keyboardShortcuts: false,
      mapTypeId: 'roadmap',
      scrollwheel: true,
    });

    clearRouteOverlays();
    polylinesRef.current = route.createPolylines();

    polylinesRef.current.forEach((polyline) => {
      polyline.setOptions({
        strokeColor: '#0078d4',
        strokeOpacity: 0.95,
        strokeWeight: 5,
        zIndex: 2,
      });
      polyline.setMap(map);
    });

    const routePath = polylinesRef.current.find((polyline) => polyline.getPath().getLength() > 0)?.getPath();

    if (routePath && routePath.getLength() > 0 && google.maps.Marker) {
      markersRef.current = [
        new google.maps.Marker({
          map,
          position: routePath.getAt(0),
          title: 'Origen',
          label: { text: 'A', color: '#ffffff', fontWeight: '700' },
          zIndex: 3,
        }),
        new google.maps.Marker({
          map,
          position: routePath.getAt(routePath.getLength() - 1),
          title: 'Destino',
          label: { text: 'B', color: '#ffffff', fontWeight: '700' },
          zIndex: 3,
        }),
      ];
    }

    if (route.viewport) {
      map.fitBounds(route.viewport, 48);
    } else {
      const bounds = new google.maps.LatLngBounds();

      polylinesRef.current.forEach((polyline) => {
        polyline.getPath().forEach((position) => bounds.extend(position));
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 48);
      }
    }

    return clearRouteOverlays;
  }, [isGoogleMapsLoaded, route]);

  return (
    <div className="form-calculadora-km__map-preview" aria-label="Vista aérea de la ruta">
      <div ref={mapContainerRef} className="form-calculadora-km__map-canvas" />
    </div>
  );
};

const GooglePlaceAutocomplete: React.FC<IGooglePlaceAutocompleteProps> = ({
  id,
  initialValue = '',
  isGoogleMapsLoaded,
  hasSelectedPlace,
  label,
  placeholder,
  onUsePersonalAddress,
  personalAddressDisabled,
  placeIdRef,
  valueRef,
  onChange,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);
  const searchRequestIdRef = useRef(0);
  const searchRevealTimeoutRef = useRef<number | null>(null);
  const [searchText, setSearchText] = useState(initialValue);
  const searchTextRef = useRef(searchText);
  const [typedSearchText, setTypedSearchText] = useState('');
  const [showSearchMore, setShowSearchMore] = useState(false);
  const [inputRevision, setInputRevision] = useState(0);
  const [searchResults, setSearchResults] = useState<google.maps.places.Place[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  onChangeRef.current = onChange;
  onErrorRef.current = onError;
  searchTextRef.current = searchText;

  const restartSearchReveal = useCallback(() => {
    if (searchRevealTimeoutRef.current !== null) {
      window.clearTimeout(searchRevealTimeoutRef.current);
      searchRevealTimeoutRef.current = null;
    }
    setShowSearchMore(false);
    setInputRevision((revision) => revision + 1);
  }, []);

  useEffect(() => {
    searchRequestIdRef.current += 1;
    if (valueRef.current && valueRef.current.value !== initialValue) {
      valueRef.current.value = initialValue;
    }
    setSearchText(initialValue);
    setSearchResults([]);
    setSearchError(null);
    setIsSearching(false);
  }, [initialValue, valueRef]);

  useEffect(() => {
    setShowSearchMore(false);
    if (!isGoogleMapsLoaded || hasSelectedPlace || typedSearchText.trim().length < 3 || typedSearchText !== searchText) {
      return;
    }

    const timeout = window.setTimeout(() => {
      searchRevealTimeoutRef.current = null;
      setShowSearchMore(true);
    }, 1000);
    searchRevealTimeoutRef.current = timeout;
    return () => {
      window.clearTimeout(timeout);
      if (searchRevealTimeoutRef.current === timeout) searchRevealTimeoutRef.current = null;
    };
  }, [hasSelectedPlace, inputRevision, isGoogleMapsLoaded, searchText, typedSearchText]);

  const searchMorePlaces = async () => {
    const query = (valueRef.current?.value ?? searchText).trim();
    if (!isGoogleMapsLoaded || query.length < 3 || isSearching) return;

    const requestId = ++searchRequestIdRef.current;
    setIsSearching(true);
    setSearchResults([]);
    setSearchError(null);

    try {
      const { Place } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;
      const { places } = await Place.searchByText({
        textQuery: query,
        fields: ['id', 'displayName', 'formattedAddress', 'businessStatus'],
        language: 'es',
        region: 'es',
        maxResultCount: 10,
      });

      if (requestId !== searchRequestIdRef.current) return;
      const selectablePlaces = places.filter((place) => place.id && (place.formattedAddress || place.displayName));
      setSearchResults(selectablePlaces);
      if (selectablePlaces.length === 0) {
        setSearchError('Google Maps no ha encontrado lugares con ese texto. Prueba con la dirección completa.');
      }
    } catch (error: unknown) {
      if (requestId !== searchRequestIdRef.current) return;
      setSearchError(getUnknownErrorMessage(error, 'No se ha podido buscar el lugar en Google Maps.'));
    } finally {
      if (requestId === searchRequestIdRef.current) setIsSearching(false);
    }
  };

  const selectSearchResult = (place: google.maps.places.Place) => {
    const address = place.formattedAddress || place.displayName || '';
    if (!place.id || !address) return;

    searchRequestIdRef.current += 1;
    placeIdRef.current = place.id;
    if (valueRef.current) valueRef.current.value = address;
    setSearchText(address);
    setTypedSearchText('');
    setSearchResults([]);
    setSearchError(null);
    setIsSearching(false);
    onChangeRef.current(address);
  };

  useEffect(() => {
    if (!isGoogleMapsLoaded || !containerRef.current) {
      return;
    }

    let autocomplete: IGooglePlaceAutocompleteElement | null = null;
    let disposed = false;

    const initializeAutocomplete = async () => {
      try {
        const placesLibrary = (await (
          google.maps.importLibrary as unknown as (libraryName: string) => Promise<unknown>
        )('places')) as IGooglePlacesNewLibrary;

        if (disposed || !containerRef.current) {
          return;
        }

        autocomplete = new placesLibrary.PlaceAutocompleteElement({
          includedRegionCodes: ['es'],
          placeholder,
          requestedLanguage: 'es',
          requestedRegion: 'es',
        });

        autocomplete.id = id;
        autocomplete.className = 'form-calculadora-km__places-new';
        Object.assign(autocomplete.style, {
          backgroundColor: 'transparent',
          border: '0',
          boxShadow: 'none',
          outline: '0',
        });
        autocomplete.description = label;
        autocomplete.noInputIcon = true;
        autocomplete.value = initialValue;
        valueRef.current = autocomplete;

        const handleInput = () => {
          searchRequestIdRef.current += 1;
          placeIdRef.current = null;
          const value = autocomplete?.value ?? '';
          restartSearchReveal();
          setSearchText(value);
          setTypedSearchText(value);
          setSearchResults([]);
          setSearchError(null);
          setIsSearching(false);
          onChangeRef.current(value);
        };

        const handleKeyDown = () => {
          restartSearchReveal();
        };

        const handleWidgetClick = () => {
          window.setTimeout(() => {
            if (autocomplete && !autocomplete.value.trim() && searchTextRef.current.trim()) handleInput();
          }, 0);
        };

        const handleSelect = async (event: Event) => {
          const placePrediction = (event as IPlacePredictionSelectEvent).placePrediction;

          if (!placePrediction) {
            return;
          }

          try {
            const place = placePrediction.toPlace();
            placeIdRef.current = place.id || placePrediction.placeId || null;

            await place.fetchFields({
              fields: ['id', 'displayName', 'formattedAddress'],
            });

            const selectedAddress =
              place.formattedAddress ||
              place.displayName ||
              placePrediction.text?.text ||
              autocomplete?.value ||
              '';

            placeIdRef.current = place.id || placeIdRef.current;

            if (autocomplete) {
              autocomplete.value = selectedAddress;
            }

            searchRequestIdRef.current += 1;
            setSearchText(selectedAddress);
            setTypedSearchText('');
            setSearchResults([]);
            setSearchError(null);
            onChangeRef.current(selectedAddress);
          } catch (error: unknown) {
            placeIdRef.current = null;
            onErrorRef.current(
              getUnknownErrorMessage(
                error,
                'No se ha podido obtener el detalle de Places API (New). Revisa que Places API (New) esté habilitada.'
              )
            );
          }
        };

        const handlePlacesError = (event: Event) => {
          const errorEvent = event as Event & { error?: { message?: string }; message?: string };

          onErrorRef.current(
            errorEvent.error?.message ||
              errorEvent.message ||
              'Places API (New) ha denegado la peticion. Revisa que Places API (New) este habilitada para esta API key.'
          );
        };

        autocomplete.addEventListener('input', handleInput);
        autocomplete.addEventListener('change', handleInput);
        autocomplete.addEventListener('keydown', handleKeyDown, true);
        autocomplete.addEventListener('click', handleWidgetClick, true);
        autocomplete.addEventListener('gmp-select', handleSelect);
        autocomplete.addEventListener('gmp-error', handlePlacesError);

        containerRef.current.replaceChildren(autocomplete);
      } catch (error: unknown) {
        onErrorRef.current(
          getUnknownErrorMessage(
            error,
            'No se ha podido inicializar Places API (New). Revisa que Maps JavaScript API y Places API (New) estén habilitadas.'
          )
        );
      }
    };

    void initializeAutocomplete();

    return () => {
      disposed = true;
      searchRequestIdRef.current += 1;
      autocomplete?.remove();
      autocomplete = null;
      valueRef.current = null;
    };
  }, [id, isGoogleMapsLoaded, label, placeholder, placeIdRef, restartSearchReveal, valueRef]);

  return (
    <div className="form-calculadora-km__places-search">
      <div className="form-calculadora-km__field-heading">
        <label htmlFor={id}>{label}</label>
        {(onUsePersonalAddress || (showSearchMore && !hasSelectedPlace)) && (
          <div className="form-calculadora-km__field-actions">
            {showSearchMore && !hasSelectedPlace && (
              <button
                type="button"
                className="form-calculadora-km__search-more-button"
                onClick={searchMorePlaces}
                disabled={!isGoogleMapsLoaded || isSearching}
              >
                <i className="fa fa-search" aria-hidden="true" />
                <span>{isSearching ? 'Buscando...' : 'Buscar más lugares'}</span>
              </button>
            )}
            {onUsePersonalAddress && (
              <button
                type="button"
                className="form-calculadora-km__personal-address-button"
                onClick={onUsePersonalAddress}
                disabled={personalAddressDisabled}
              >
                <i className="fa fa-home" aria-hidden="true" />
                <span>Domicilio Personal</span>
              </button>
            )}
          </div>
        )}
      </div>
      <div ref={containerRef} className="form-calculadora-km__places-container">
        {!isGoogleMapsLoaded && (
          <input
            id={id}
            type="text"
            placeholder="Cargando buscador..."
            disabled
            aria-label={label}
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
        )}
      </div>
      {(searchResults.length > 0 || searchError) && (
        <div className="form-calculadora-km__search-results">
          {searchError ? (
            <p role="status">{searchError}</p>
          ) : (
            <ul aria-label={`Resultados adicionales de Google Maps para ${label}`}>
              {searchResults.map((place) => (
                <li key={place.id}>
                  <button type="button" onClick={() => selectSearchResult(place)}>
                    <strong>{place.displayName || place.formattedAddress}</strong>
                    {place.formattedAddress && <span>{place.formattedAddress}</span>}
                    {place.businessStatus === google.maps.places.BusinessStatus.CLOSED_PERMANENTLY && (
                      <span>Cerrado permanentemente</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <span className="form-calculadora-km__search-attribution">Resultados de Google Maps</span>
        </div>
      )}
    </div>
  );
};

interface IFormCalculadoraKilometrajeProps {
  name: string;
  userId: string | null;
  id?: string;
  label?: string;
  placeholder?: string;
  req?: string | boolean;
  required?: string | boolean;
  additionalRequiredNames?: string[];
  invisible?: boolean;
  xl?: number;
  md?: number;
  col?: number;
  disabled?: boolean;
}

const FormCalculadoraKilometraje: React.FC<IFormCalculadoraKilometrajeProps> = ({
  name,
  userId,
  id,
  label = 'Viajes en vehículo propio',
  req,
  required,
  additionalRequiredNames = [],
  invisible = false,
  xl = 12,
  md = 12,
  col = 12,
  disabled = false,
}) => {
  const {
    control,
    formState: { errors },
    getValues,
    trigger,
  } = useFormContext();

  const requiredValue = req ?? required ?? false;
  const requiredMessage = requiredValue === true ? 'Debe añadir al menos un viaje' : requiredValue || undefined;
  const routeInfoId = `${id ?? name}-info-ruta`;
  const modalBaseId = `${id ?? name}-modal`;
  const hasRequiredRows = (value: unknown) => Array.isArray(value) && value.length > 0;

  const { fields, append, remove } = useFieldArray({
    control,
    name,
    rules: requiredMessage
      ? {
        validate: (value) => (
          hasRequiredRows(value) || additionalRequiredNames.some((fieldName) => hasRequiredRows(getValues(fieldName)))
            ? true
            : requiredMessage
        ),
      }
      : undefined,
  });

  const tripRows = fields as unknown as Array<IVehicleTripValue & { id: string }>;
  const rawErrorMessage = get(errors, `${name}.root.message`) || get(errors, `${name}.message`);
  const errorMessage = typeof rawErrorMessage === 'string' ? rawErrorMessage : undefined;

  const [showModal, setShowModal] = useState(false);
  const [showRouteInfo, setShowRouteInfo] = useState(false);
  const [travelDate, setTravelDate] = useState('');
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [originAddress, setOriginAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [originHasSelectedPlace, setOriginHasSelectedPlace] = useState(false);
  const [destinationHasSelectedPlace, setDestinationHasSelectedPlace] = useState(false);
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [modalValidationErrors, setModalValidationErrors] = useState<IModalValidationErrors>({});
  const [calculatedTrip, setCalculatedTrip] = useState<ICalculatedTrip | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<IGoogleRoute | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const autocompleteOriginRef = useRef<IGooglePlaceAutocompleteElement | null>(null);
  const autocompleteDestinationRef = useRef<IGooglePlaceAutocompleteElement | null>(null);
  const placeIdOriginRef = useRef<string | null>(null);
  const placeIdDestinationRef = useRef<string | null>(null);

  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_REACT_APP_GOOGLE_MAPS_API_KEY || '',
    language: 'es',
    region: 'ES',
    version: 'weekly',
  });

  const {
    getInfoKilometrajeSolicitudGastosTransporte,
    resetInfoKilometrajeSolicitudGastosTransporte,
    loadingGetInfoKilometrajeSolicitudGastosTransporte,
    errorGetInfoKilometrajeSolicitudGastosTransporte,
    dataGetInfoKilometrajeSolicitudGastosTransporte,
  } = useGetInfoKilometrajeSolicitudGastosTransporte();
  const mileageRate = parseNumberValue(
    dataGetInfoKilometrajeSolicitudGastosTransporte?.ImporteKilometraje
  );

  const calculatedAmount = calculatedTrip
    && mileageRate !== null
    ? calculateTripAmount(calculatedTrip.kilometers, mileageRate, isRoundTrip)
    : null;
  const modalValidationMessages = getValidationMessages(modalValidationErrors);

  const clearCalculatedRoute = () => {
    setCalculatedTrip(null);
    setSelectedRoute(null);
  };

  const getCurrentRouteValues = () => ({
    origin: (autocompleteOriginRef.current?.value || originAddress).trim(),
    destination: (autocompleteDestinationRef.current?.value || destinationAddress).trim(),
  });

  const clearModalValidationError = (...keys: Array<keyof IModalValidationErrors>) => {
    setModalValidationErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      keys.forEach((key) => {
        delete nextErrors[key];
      });

      return nextErrors;
    });
  };

  const validateModalForm = ({
    requireDate = false,
    requireCalculation = false,
  }: {
    requireDate?: boolean;
    requireCalculation?: boolean;
  }) => {
    const { origin, destination } = getCurrentRouteValues();
    const nextErrors: IModalValidationErrors = {};

    if (!requireDate && modalValidationErrors.travelDate) {
      nextErrors.travelDate = modalValidationErrors.travelDate;
    }

    if (requireDate && !travelDate) {
      nextErrors.travelDate = 'Te falta seleccionar la fecha del viaje.';
    } else if (travelDate && travelDate > getTodayInputValue()) {
      nextErrors.travelDate = 'La fecha del viaje no puede ser futura.';
    }

    if (!origin) {
      nextErrors.origin = 'Te falta indicar el origen del viaje.';
    }

    if (!destination) {
      nextErrors.destination = 'Te falta indicar el destino del viaje.';
    }

    if (requireCalculation && mileageRate === null) {
      nextErrors.calculation = 'Selecciona una fecha con un precio por kilómetro válido.';
    } else if (requireCalculation && (!calculatedTrip || calculatedAmount === null)) {
      nextErrors.calculation = 'Te falta calcular el kilometraje antes de añadir el viaje.';
    }

    setModalValidationErrors(nextErrors);

    return {
      isValid: Object.keys(nextErrors).length === 0,
      origin,
      destination,
    };
  };

  const resetModalForm = () => {
    setTravelDate('');
    setIsRoundTrip(false);
    setOriginAddress('');
    setDestinationAddress('');
    setOriginHasSelectedPlace(false);
    setDestinationHasSelectedPlace(false);
    setSearchMessage(null);
    setModalValidationErrors({});
    setLoadingCalculation(false);
    resetInfoKilometrajeSolicitudGastosTransporte();
    clearCalculatedRoute();
    placeIdOriginRef.current = null;
    placeIdDestinationRef.current = null;

    if (autocompleteOriginRef.current) {
      autocompleteOriginRef.current.value = '';
    }

    if (autocompleteDestinationRef.current) {
      autocompleteDestinationRef.current.value = '';
    }
  };

  const openAddModal = () => {
    resetModalForm();
    setShowRouteInfo(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setShowRouteInfo(false);
    setLoadingCalculation(false);
    resetInfoKilometrajeSolicitudGastosTransporte();
  };

  const handleTravelDateChange = (value: string) => {
    setTravelDate(value);
    clearCalculatedRoute();
    setSearchMessage(null);
    clearModalValidationError('travelDate', 'calculation');
    resetInfoKilometrajeSolicitudGastosTransporte();

    if (!value || value > getTodayInputValue()) return;

    if (!userId) {
      setModalValidationErrors((currentErrors) => ({
        ...currentErrors,
        calculation: 'No se ha podido identificar al usuario para consultar el precio por kilómetro.',
      }));
      return;
    }

    void getInfoKilometrajeSolicitudGastosTransporte(userId, value).catch(() => undefined);
  };

  const handleRouteInputChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    field: 'origin' | 'destination'
  ) => (value: string) => {
    setter(value);
    if (field === 'origin') {
      setOriginHasSelectedPlace(Boolean(placeIdOriginRef.current));
    } else {
      setDestinationHasSelectedPlace(Boolean(placeIdDestinationRef.current));
    }
    clearCalculatedRoute();
    setSearchMessage(null);
    clearModalValidationError('origin', 'destination', 'calculation');
  };

  const getPersonalAddress = () => {
    const datosPersonales = getValues('DatosPersonales') || {};
    const addressParts = [
      datosPersonales?.SolicitanteDomicilio,
      datosPersonales?.SolicitanteCodigoPostal,
      datosPersonales?.SolicitanteComunidadCodigoDescripcion,
      datosPersonales?.SolicitanteProvinciaDescripcion,
    ];

    return addressParts
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .join(', ');
  };

  const applyPersonalAddressToField = async (field: 'origin' | 'destination') => {
    const personalAddress = getPersonalAddress();

    if (!personalAddress) {
      setSearchMessage('No hay domicilio personal informado en Datos Personales.');
      return;
    }

    const isOrigin = field === 'origin';
    const autocompleteRef = isOrigin ? autocompleteOriginRef : autocompleteDestinationRef;
    const placeIdRef = isOrigin ? placeIdOriginRef : placeIdDestinationRef;
    const setHasSelectedPlace = isOrigin ? setOriginHasSelectedPlace : setDestinationHasSelectedPlace;
    const setFieldValue = (value: string) => {
      (isOrigin ? setOriginAddress : setDestinationAddress)(value);
      if (autocompleteRef.current) {
        autocompleteRef.current.value = value;
      }
    };

    setFieldValue(personalAddress);
    placeIdRef.current = null;
    setHasSelectedPlace(false);
    clearCalculatedRoute();
    setSearchMessage(null);
    clearModalValidationError(field, 'calculation');

    if (!isGoogleMapsLoaded || googleMapsLoadError || typeof google === 'undefined' || !google.maps?.Geocoder) {
      return;
    }

    try {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({ address: personalAddress, region: 'ES' });
      const firstResult = response.results?.[0];

      if (firstResult) {
        const resolvedAddress = firstResult.formatted_address || personalAddress;
        setFieldValue(resolvedAddress);
        placeIdRef.current = firstResult.place_id || null;
        setHasSelectedPlace(Boolean(placeIdRef.current));
      }
    } catch (error: unknown) {
      placeIdRef.current = null;
      setHasSelectedPlace(false);
      setSearchMessage(
        getUnknownErrorMessage(
          error,
          'Se ha escrito el domicilio personal, pero Google Maps no ha podido validarlo.'
        )
      );
    }
  };

  const calculateDistance = async () => {
    const validationResult = validateModalForm({ requireCalculation: false });

    if (!validationResult.isValid) {
      clearCalculatedRoute();
      setSearchMessage(null);
      return;
    }

    const { origin, destination } = validationResult;

    if (googleMapsLoadError) {
      clearCalculatedRoute();
      setSearchMessage('No se ha podido cargar Google Maps. Revisa la API key.');
      return;
    }

    if (!isGoogleMapsLoaded || typeof google === 'undefined' || !google.maps?.importLibrary) {
      clearCalculatedRoute();
      setSearchMessage('Google Maps aún no está listo para calcular la ruta.');
      return;
    }

    setLoadingCalculation(true);
    clearCalculatedRoute();
    setSearchMessage(null);

    try {
      if (placeIdOriginRef.current || placeIdDestinationRef.current) {
        await google.maps.importLibrary('places');
      }

      const routesLibrary = (await (
        google.maps.importLibrary as unknown as (libraryName: string) => Promise<unknown>
      )('routes')) as IGoogleRoutesLibrary;

      const routeOrigin = placeIdOriginRef.current
        ? new google.maps.places.Place({ id: placeIdOriginRef.current })
        : origin;
      const routeDestination = placeIdDestinationRef.current
        ? new google.maps.places.Place({ id: placeIdDestinationRef.current })
        : destination;

      const { routes } = await routesLibrary.Route.computeRoutes({
        origin: routeOrigin,
        destination: routeDestination,
        travelMode: 'DRIVING',
        routingPreference: 'TRAFFIC_UNAWARE',
        fields: ['distanceMeters', 'path', 'viewport'],
        language: 'es',
        region: 'es',
      });

      const route = routes?.[0];
      const distanceMeters = route?.distanceMeters;

      if (typeof distanceMeters !== 'number') {
        throw new Error('No se ha encontrado una ruta por carretera entre las ubicaciones indicadas.');
      }

      const kilometers = distanceMeters / 1000;

      setOriginAddress(origin);
      setDestinationAddress(destination);
      setCalculatedTrip({ origin, destination, kilometers });
      setSelectedRoute(route ?? null);
      clearModalValidationError('origin', 'destination', 'calculation');
    } catch (error: unknown) {
      clearCalculatedRoute();
      setSearchMessage(
        getUnknownErrorMessage(
          error,
          'No se ha podido calcular la distancia. Revisa que Routes API esté habilitada para esta clave.'
        )
      );
    } finally {
      setLoadingCalculation(false);
    }
  };

  const addTrip = () => {
    const validationResult = validateModalForm({ requireDate: true, requireCalculation: true });

    if (!validationResult.isValid || !calculatedTrip || calculatedAmount === null) {
      setSearchMessage(null);
      return;
    }

    const trip: IVehicleTripValue = {
      FechaViaje: travelDate,
      IdaVuelta: isRoundTrip,
      DireccionOrigen: calculatedTrip.origin,
      DireccionDestino: calculatedTrip.destination,
      Kilometros: roundToTwoDecimals(calculatedTrip.kilometers),
      PrecioKm: mileageRate,
      Importe: calculatedAmount,
    };

    append(trip);
  void trigger([name, ...additionalRequiredNames]);
    closeModal();
  };

  const removeTrip = (rowIndex: number) => {
    remove(rowIndex);
    setHoveredRow(null);
    void trigger([name, ...additionalRequiredNames]);
  };

  return (
    <div className={`col-xl-${xl} col-md-${md} col-${col} ${invisible ? 'd-none' : ''}`}>
      <div data-input-name={name} className={`form-calculadora-km ${errorMessage ? 'error' : ''}`}>
        <div className="form-calculadora-km__table-block">
          <div className="form-calculadora-km__table-header">
            <div className="form-calculadora-km__table-heading">
              <label className="form-calculadora-km__label">{label}</label>
              <span className="form-calculadora-km__table-count">
                {tripRows.length === 1 ? '1 viaje añadido' : `${tripRows.length} viajes añadidos`}
              </span>
            </div>

            <button
              type="button"
              className="form-calculadora-km__add-trip-button"
              onClick={openAddModal}
              disabled={disabled}
            >
              <span aria-hidden="true">+</span>
              <span>Agregar</span>
            </button>
          </div>

          <div className="form-calculadora-km__table-shell">
            <div className="form-calculadora-km__table-scroll">
              <table className="form-calculadora-km__table">
                <thead>
                  <tr>
                    <th>Fecha viaje</th>
                    <th>Ida y vuelta</th>
                    <th>Origen</th>
                    <th>Destino</th>
                    <th>Kilómetros</th>
                    <th>Precio Km</th>
                    <th>Importe</th>
                    <th className="form-calculadora-km__actions-head" aria-label="Acciones" />
                  </tr>
                </thead>

                <tbody>
                  {tripRows.map((trip, rowIndex) => (
                    <tr
                      key={trip.id}
                      className={hoveredRow === rowIndex ? 'form-calculadora-km__row--remove-hover' : ''}
                    >
                      <td data-label="Fecha viaje">{formatDateForDisplay(trip.FechaViaje)}</td>
                      <td data-label="Ida y vuelta">
                        <span className={`form-calculadora-km__chip ${trip.IdaVuelta ? 'is-active' : ''}`}>
                          {trip.IdaVuelta ? 'Sí' : 'No'}
                        </span>
                      </td>
                      <td data-label="Origen" className="form-calculadora-km__route-cell">
                        {trip.DireccionOrigen || '-'}
                      </td>
                      <td data-label="Destino" className="form-calculadora-km__route-cell">
                        {trip.DireccionDestino || '-'}
                      </td>
                      <td data-label="Kilómetros">{formatKilometers(trip.Kilometros)}</td>
                      <td data-label="Precio Km">{formatPricePerKm(trip.PrecioKm)}</td>
                      <td data-label="Importe" className="form-calculadora-km__money">
                        {formatMoney(trip.Importe)}
                      </td>
                      <td className="form-calculadora-km__actions-cell" data-label="Acciones">
                        <button
                          type="button"
                          className="form-calculadora-km__remove-button"
                          onClick={() => removeTrip(rowIndex)}
                          onMouseEnter={() => setHoveredRow(rowIndex)}
                          onMouseLeave={() => setHoveredRow(null)}
                          aria-label={`Eliminar viaje ${rowIndex + 1}`}
                          disabled={disabled}
                        >
                          <img src={Delete} alt="" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))}

                  {tripRows.length === 0 && (
                    <tr className="form-calculadora-km__empty-row">
                      <td colSpan={8}>Sin viajes añadidos.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {errorMessage && <p className="form-calculadora-km__error m-0">{errorMessage}</p>}
        </div>

        <Modal show={showModal} onHide={closeModal} className="form-calculadora-km" centered>
        <Modal.Header closeButton className="form-calculadora-km__modal-header">
          <Modal.Title>
            <span className="form-calculadora-km__modal-title-icon" aria-hidden="true">
              <span className="e-icons e-calculation" />
            </span>
            <span className="form-calculadora-km__modal-title-text">Nuevo viaje en vehículo propio</span>
            <button
              type="button"
              className="form-calculadora-km__info-button"
              aria-label={showRouteInfo ? 'Ocultar información de la calculadora' : 'Mostrar información de la calculadora'}
              aria-controls={routeInfoId}
              aria-expanded={showRouteInfo}
              title="Más información"
              onClick={() => setShowRouteInfo((currentValue) => !currentValue)}
            >
              <span className="information-icon-orange p-0" aria-hidden="true" />
            </button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-calculadora-km__modal-body">
            {showRouteInfo && (
              <div id={routeInfoId} className="form-calculadora-km__info-panel">
                <span className="information-icon-orange p-0 form-calculadora-km__info-panel-icon" aria-hidden="true" />
                <p>
                  Indica la fecha, el origen y el destino del desplazamiento. La distancia se calcula por carretera y
                  el importe se obtiene con el precio por kilómetro vigente.
                </p>
              </div>
            )}

            <div className="form-calculadora-km__trip-grid">
              <div className="form-calculadora-km__modal-field">
                <label htmlFor={`${modalBaseId}-fecha`}>Fecha viaje</label>
                <input
                  id={`${modalBaseId}-fecha`}
                  type="date"
                  max={getTodayInputValue()}
                  value={travelDate}
                  onChange={(event) => {
                    handleTravelDateChange(event.target.value);
                  }}
                  className={`form-calculadora-km__modal-input ${modalValidationErrors.travelDate ? 'is-invalid' : ''}`}
                  aria-invalid={modalValidationErrors.travelDate ? true : undefined}
                  aria-describedby={modalValidationErrors.travelDate ? `${modalBaseId}-fecha-error` : undefined}
                />
                {modalValidationErrors.travelDate && (
                  <p id={`${modalBaseId}-fecha-error`} className="form-calculadora-km__field-error">
                    {modalValidationErrors.travelDate}
                  </p>
                )}
              </div>

              <label className="form-calculadora-km__toggle-card" htmlFor={`${modalBaseId}-ida-vuelta`}>
                <input
                  id={`${modalBaseId}-ida-vuelta`}
                  type="checkbox"
                  checked={isRoundTrip}
                  onChange={(event) => setIsRoundTrip(event.target.checked)}
                  className="form-calculadora-km__toggle-input"
                />
                <span className="form-calculadora-km__toggle-box" aria-hidden="true" />
                <span className="form-calculadora-km__toggle-text">
                  <strong>Ida y vuelta</strong>
                  <span>Aplicar el trayecto en ambos sentidos</span>
                </span>
              </label>
            </div>

            {modalValidationMessages.length > 0 && (
              <div className="form-calculadora-km__validation-summary" role="alert" aria-live="polite">
                <strong>Revisa el viaje antes de añadirlo</strong>
                <ul>
                  {modalValidationMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-calculadora-km__route-panel">
              <div className="form-calculadora-km__route-marker" aria-hidden="true">
                <span className="form-calculadora-km__route-dot is-origin" />
                <span className="form-calculadora-km__route-line" />
                <span className="form-calculadora-km__route-dot is-destination" />
              </div>

              <div className="form-calculadora-km__route-fields">
                <div className={`form-calculadora-km__modal-field ${modalValidationErrors.origin ? 'form-calculadora-km__modal-field--error' : ''}`}>
                  <GooglePlaceAutocomplete
                    id={`${modalBaseId}-origen`}
                    initialValue={originAddress}
                    isGoogleMapsLoaded={isGoogleMapsLoaded}
                    hasSelectedPlace={originHasSelectedPlace}
                    label="Origen"
                    placeholder="Ejemplo: Toledo"
                    onUsePersonalAddress={() => void applyPersonalAddressToField('origin')}
                    personalAddressDisabled={disabled}
                    placeIdRef={placeIdOriginRef}
                    valueRef={autocompleteOriginRef}
                    onChange={handleRouteInputChange(setOriginAddress, 'origin')}
                    onError={setSearchMessage}
                  />
                  {modalValidationErrors.origin && (
                    <p className="form-calculadora-km__field-error">{modalValidationErrors.origin}</p>
                  )}
                </div>

                <div className={`form-calculadora-km__modal-field ${modalValidationErrors.destination ? 'form-calculadora-km__modal-field--error' : ''}`}>
                  <GooglePlaceAutocomplete
                    id={`${modalBaseId}-destino`}
                    initialValue={destinationAddress}
                    isGoogleMapsLoaded={isGoogleMapsLoaded}
                    hasSelectedPlace={destinationHasSelectedPlace}
                    label="Destino"
                    placeholder="Ejemplo: Madrid"
                    onUsePersonalAddress={() => void applyPersonalAddressToField('destination')}
                    personalAddressDisabled={disabled}
                    placeIdRef={placeIdDestinationRef}
                    valueRef={autocompleteDestinationRef}
                    onChange={handleRouteInputChange(setDestinationAddress, 'destination')}
                    onError={setSearchMessage}
                  />
                  {modalValidationErrors.destination && (
                    <p className="form-calculadora-km__field-error">{modalValidationErrors.destination}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="form-calculadora-km__calculated-panel">
              <div className="form-calculadora-km__calculated-field">
                <label htmlFor={`${modalBaseId}-kilometros`}>Kilómetros</label>
                <input
                  id={`${modalBaseId}-kilometros`}
                  type="text"
                  value={calculatedTrip ? formatKilometers(calculatedTrip.kilometers) : 'Pendiente'}
                  readOnly
                  className="form-calculadora-km__calculated-input"
                />
              </div>

              <div className="form-calculadora-km__calculated-field">
                <label htmlFor={`${modalBaseId}-precio-km`}>Precio Km</label>
                {loadingGetInfoKilometrajeSolicitudGastosTransporte ? (
                  <div
                    id={`${modalBaseId}-precio-km`}
                    className="form-calculadora-km__calculated-input form-calculadora-km__calculated-input--loading"
                    role="status"
                    aria-live="polite"
                  >
                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    <span>Consultando precio...</span>
                  </div>
                ) : (
                  <input
                    id={`${modalBaseId}-precio-km`}
                    type="text"
                    value={mileageRate === null ? 'Pendiente' : formatPricePerKm(mileageRate)}
                    readOnly
                    className="form-calculadora-km__calculated-input"
                  />
                )}
              </div>

              <div className="form-calculadora-km__calculated-field">
                <label htmlFor={`${modalBaseId}-importe`}>Importe</label>
                <input
                  id={`${modalBaseId}-importe`}
                  type="text"
                  value={calculatedAmount === null ? 'Pendiente' : formatMoney(calculatedAmount)}
                  readOnly
                  className="form-calculadora-km__calculated-input is-amount"
                />
              </div>
            </div>

            {modalValidationErrors.calculation && (
              <p className="form-calculadora-km__field-error form-calculadora-km__field-error--standalone" role="alert">
                {modalValidationErrors.calculation}
              </p>
            )}

            {errorGetInfoKilometrajeSolicitudGastosTransporte && (
              <p className="form-calculadora-km__error m-0" role="alert">
                {errorGetInfoKilometrajeSolicitudGastosTransporte}
              </p>
            )}

            <div className="form-calculadora-km__action-row">
              <div className="form-calculadora-km__action-buttons">
                <button
                  type="button"
                  className="form-calculadora-km__calculate-button"
                  onClick={calculateDistance}
                  disabled={loadingCalculation || loadingGetInfoKilometrajeSolicitudGastosTransporte || mileageRate === null || !isGoogleMapsLoaded}
                >
                  {loadingCalculation ? (
                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                  ) : (
                    <span className="e-icons e-calculation form-calculadora-km__icon" aria-hidden="true" />
                  )}
                  <span>{loadingCalculation ? 'Calculando...' : 'Calcular kilometraje'}</span>
                </button>

                <button
                  type="button"
                  className="form-calculadora-km__accept-button"
                  onClick={addTrip}
                  disabled={loadingCalculation || loadingGetInfoKilometrajeSolicitudGastosTransporte}
                >
                  Añadir
                </button>
              </div>
            </div>

            {!isGoogleMapsLoaded && !googleMapsLoadError && (
              <div className="form-calculadora-km__status">Cargando Google Maps...</div>
            )}

            {googleMapsLoadError && (
              <p className="form-calculadora-km__error m-0">
                No se pudo cargar Google Maps. Revisa la API key.
              </p>
            )}

            {!loadingCalculation && selectedRoute && (
              <div className="form-calculadora-km__map-shell">
                <RoutePreviewMap isGoogleMapsLoaded={isGoogleMapsLoaded} route={selectedRoute} />
              </div>
            )}

            {!loadingCalculation && searchMessage && (
              <p className="form-calculadora-km__error m-0">{searchMessage}</p>
            )}
          </div>
        </Modal.Body>
      </Modal>
      </div>
    </div>
  );
};

export default FormCalculadoraKilometraje;
